import { useEffect, useState } from 'react';
import { KeyOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, InputNumber, Popconfirm, Radio, Switch, Tag } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
const ENCRYPTION = {
    starttls: { label: 'STARTTLS', detail: 'Port 587. The usual choice for Gmail, Zoho, Outlook and most hosts.' },
    ssl: { label: 'SSL', detail: 'Port 465. Implicit TLS from the first byte.' },
    none: { label: 'None', detail: 'No encryption. Only for a mail server on the same private network.' }
};
const FIELDS = ['enabled', 'host', 'port', 'encryption', 'username', 'fromName', 'fromEmail'];
/**
 * Settings › Email: the SMTP host that registration codes go out with. Saved in the database; the password is
 * encrypted and never shown again. After it is saved and a test arrives, switch it on.
 */
export function EmailSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/email').then((body) => body.settings));
    const settings = data;
    const encryption = Form.useWatch('encryption', form) ?? settings?.encryption ?? 'starttls';
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ ...Object.fromEntries(FIELDS.map((key) => [key, settings[key]])), password: '', testTo: form.getFieldValue('testTo') ?? '' });
        setDirty(false);
    }, [settings, form]);
    const values = () => {
        const all = form.getFieldsValue();
        return { ...Object.fromEntries(FIELDS.map((key) => [key, all[key]])), password: all.password ?? '' };
    };
    async function save(clearPassword = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/email', { method: 'PUT', body: JSON.stringify({ ...values(), clearPassword, ...(clearPassword ? { enabled: false } : {}) }) });
            message.success(clearPassword ? 'Password removed. Codes are not sent by email.' : body.settings.active ? 'Saved. Registration codes go out by email.' : 'Saved. Email is off.');
            setTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function sendTest() {
        const to = String(form.getFieldValue('testTo') ?? '').trim();
        if (!to) {
            message.error('Enter the email address to send the test to.');
            return;
        }
        setTesting(true);
        setTest(null);
        try {
            const result = await api('/v1/admin/settings/email/test', { method: 'POST', body: JSON.stringify({ ...values(), to }) });
            setTest({ ok: true, text: `Sent to ${result.sentTo}. The test code is ${result.code}.` });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'Could not send the test email' });
        }
        finally {
            setTesting(false);
        }
    }
    const on = Boolean(settings?.active);
    return (<>
      <PageHeader title="Email" description="Registration codes for the account panel are sent by email through SMTP. Kivonex makes and checks the codes; your mail server only delivers them. Configure the host here, send a test, then switch it on." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      {settings?.passwordUnreadable && (<Alert type="warning" showIcon title="The saved SMTP password cannot be read on this server" description="The server's settings key was replaced or lost, so codes are not sent by email until the password is entered again." style={{ marginBottom: 20 }}/>)}
      {settings && settings.enabled && !on && (<Alert type="warning" showIcon title="Email is switched on but not complete" description={settings.missing} style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="Registration codes" tone={on ? 'green' : 'slate'} icon={<MailOutlined />} loading={loading && !data} value={on ? 'By email' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="SMTP host" tone="indigo" icon={<SendOutlined />} loading={loading && !data} value={<span style={{ fontSize: 18 }}>{settings?.host || '—'}</span>} foot={settings?.host ? `Port ${settings.port} · ${ENCRYPTION[settings.encryption]?.label}` : 'No host yet'}/>
        <StatCard label="From" tone="amber" icon={<MailOutlined />} loading={loading && !data} value={<span style={{ fontSize: 16 }}>{settings?.fromEmail || '—'}</span>} foot={settings?.fromName || 'No from address yet'}/>
      </div>

      {loading && !data ? (<Panel flush title="SMTP" description="The mail server that sends registration codes">
          <FormSkeleton sections={3}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={() => void save()} onValuesChange={(changed) => {
                if (Object.keys(changed).some((key) => key !== 'testTo'))
                    setDirty(true);
            }}>
        <Panel flush title="SMTP" description="Any mail host that accepts SMTP (Gmail, Zoho, Outlook, your own server)" extra={on ? <Tag color="success" variant="filled"><span className="status-dot"/>On</Tag> : <Tag variant="filled">Off</Tag>}>
          <div className="form-section">
            <div>
              <h4>Send codes by email</h4>
              <p>While this is off, codes are not sent by email (a development server writes them to its own log). Switch it on only after a test email arrives.</p>
            </div>
            <div>
              <Form.Item name="enabled" valuePropName="checked" label="Registration codes by email">
                <Switch />
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Server</h4>
              <p>Host and port from your mail provider. STARTTLS on 587 is the usual choice.</p>
            </div>
            <div>
              <div className="form-row">
                <Form.Item label="SMTP host" name="host" extra="For example smtp.gmail.com or smtp.zoho.in.">
                  <Input className="mono" placeholder="smtp.example.com" autoComplete="off"/>
                </Form.Item>
                <Form.Item label="Port" name="port">
                  <InputNumber placeholder="587" min={1} max={65535} style={{ width: '100%' }}/>
                </Form.Item>
              </div>
              <Form.Item name="encryption" extra={ENCRYPTION[encryption]?.detail}>
                <Radio.Group className="sms-routes">
                  {Object.entries(ENCRYPTION).map(([value, item]) => (<Radio key={value} value={value}>
                      <b>{item.label}</b>
                      <div className="cell-sub">{item.detail}</div>
                    </Radio>))}
                </Radio.Group>
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Sign-in</h4>
              <p>Username and password for the mail server. The password is stored encrypted and never shown again. Leave both blank only if the server accepts mail with no login.</p>
            </div>
            <div>
              <Form.Item label="Username" name="username" extra="Often the same as the from address.">
                <Input autoComplete="off" placeholder="noreply@example.com"/>
              </Form.Item>
              <Form.Item label="Password" name="password" extra={settings?.passwordSet ? <span><KeyOutlined style={{ color: 'var(--brand)' }}/> A password ending <span className="mono">{settings.passwordHint}</span> is saved. Leave blank to keep it.</span> : 'App password if the host uses two-factor sign-in (Gmail, Zoho).'}>
                <Input.Password autoComplete="new-password" placeholder={settings?.passwordSet ? '••••••••••••' : 'SMTP password'} className="mono"/>
              </Form.Item>
              {settings?.passwordSet && (<Popconfirm title="Remove the saved password?" description="Registration codes stop going out by email until a password is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(true)}>
                  <Button danger type="link" style={{ paddingInline: 0 }}>Remove password</Button>
                </Popconfirm>)}
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>From</h4>
              <p>The name and address customers see on the code email. The address must be allowed by the mail server.</p>
            </div>
            <div className="form-row">
              <Form.Item label="From name" name="fromName">
                <Input placeholder="Kivonex"/>
              </Form.Item>
              <Form.Item label="From email" name="fromEmail" rules={[{ type: 'email', message: 'Enter a valid from address.' }]}>
                <Input type="email" placeholder="noreply@example.com"/>
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Test</h4>
              <p>Sends one email with the settings above, saved or not. The test code signs nobody in.</p>
            </div>
            <div className="form-row">
              <Form.Item label="Send test to" name="testTo">
                <Input type="email" placeholder="you@example.com"/>
              </Form.Item>
              <Form.Item label=" ">
                <Button icon={<SendOutlined />} onClick={() => void sendTest()} loading={testing}>Send test email</Button>
              </Form.Item>
            </div>
          </div>

          {test && (<div style={{ padding: '0 20px 20px' }}>
              <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
            </div>)}
          <div className="form-actions">
            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save changes</Button>
          </div>
        </Panel>
      </Form>)}
    </>);
}
